import { createContext, useContext } from 'react'
import { LDProvider, useFlags } from 'launchdarkly-react-client-sdk'
import type { LDClient, LDFlagSet } from 'launchdarkly-js-client-sdk'

// Mirrors FEATURE_FLAGS_AVAILABLE in STO's feature-flag.js service.
// Add every LaunchDarkly flag key here before using it in the codebase.
// TypeScript will catch unknown keys at compile time via the FeatureFlagKey type.
export const FEATURE_FLAGS_AVAILABLE = [
	'sto-loyalty',
	'sto-item-images',
	'sto-account-create',
	'sto-customer-account-remember-information',
	'sto-account-manage',
	'sto-nested-mods',
	'sto-quantity-on-hand',
	'sto-order-confirmation-message',
	'sto-meta-pixel',
	'sto-referrals',
	'sto-cvv-validation',
	'sto-skip-order-settings',
	'sto-drop-off-points',
	'sto-dm-loyalty',
	'sto-customer-account-reorder',
	'sto-enable-google-auto-complete',
	'sto-google-conversion-tracking',
	'sto-i4go-beta-url',
] as const

export type FeatureFlagKey = (typeof FEATURE_FLAGS_AVAILABLE)[number]

interface FeatureFlagProviderProps {
	ldClient: LDClient | null
	children: React.ReactNode
}

const FeatureFlagContext = createContext<LDFlagSet>({})

function LDFlagsBridge({ children }: { children: React.ReactNode }) {
	const flags = useFlags()
	return <FeatureFlagContext.Provider value={flags}>{children}</FeatureFlagContext.Provider>
}

export function FeatureFlagProvider({ ldClient, children }: FeatureFlagProviderProps) {
	if (!ldClient) {
		return <FeatureFlagContext.Provider value={{}}>{children}</FeatureFlagContext.Provider>
	}

	// clientSideID is required by LDProvider's TS types but ignored at runtime when
	// ldClient is provided — the SDK uses the existing client and skips re-initialisation.
	return (
		<LDProvider clientSideID={import.meta.env.VITE_LAUNCHDARKLY_CLIENT_ID} ldClient={ldClient}>
			<LDFlagsBridge>{children}</LDFlagsBridge>
		</LDProvider>
	)
}

// Typed wrapper so the rest of the codebase never imports the LD SDK directly.
// IMPORTANT: always pass the "safe off" / feature-disabled state as defaultValue — it is returned
// in every environment where LD credentials are absent, the flag is missing, or an error occurs.
// Mirrors STO's LaunchDarklyService.isEnabled(flagKey).
export function useFeatureFlag<T>(flagKey: FeatureFlagKey, defaultValue: T): T {
	const flags = useContext(FeatureFlagContext)

	try {
		const value: unknown = flags[flagKey]
		return value !== undefined ? (value as T) : defaultValue
	} catch {
		// If the context is in an unexpected state or flag access throws,
		// fall back to the safe default so the UI never crashes on a flag read.
		return defaultValue
	}
}
